/** @jsx jsx */
import { jsx } from '@emotion/core';
import PropTypes from 'prop-types';
import { Image, withSitecoreContext } from '@sitecore-jss/sitecore-jss-react';
import 'intersection-observer'; // polyfill
import Observer from '@researchgate/react-intersection-observer';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import SourceTag from './sourceTag';
import {
  buildImagePath,
  parseUrlConfigParams,
} from '../../../utils/cloudinary';
import { SafePureComponent } from '../../../utils/SafePureComponent';

const isCloudinaryEnabled = process.env.REACT_APP_CLOUDINARY_ENABLED || 'false';

/**
 * This component can render different elements based on params.
 * SiteCore image: if sitecoreContext.pageEditing is true.
 * Picture element: needs image.imageSizes obj configuration with cloudinary variables for each breakpoint.
 * Image source: if image.isClientLink is true, it sets the url as absolute without the cloudinary base url.
 * Image parameters can be checked in https://cloudinary.com/documentation/image_transformation_reference
 *
 * Component that renders an image. It has a configuration to apply Cloudinary when
 * the flags are enabled.
 *
 * IMPORTANT: Cloudinary doesn't apply for local development. There's an example in
 * the storybook but can only be checked if isCloudinaryDisabled is false. Make the change
 * manually to check that the feature works correctly.
 */
export class Picture extends SafePureComponent {
  constructor(props) {
    super(props);
    this.state = {
      imageSource: this.props.fallBack ? null : this.getImageSource(),
      sourceError: false,
      visibility: false,
    };
  }

  componentDidMount() {
    this.setState({ imageSource: this.getImageSource() });
  }

  createSourceTags() {
    const { image, imageSizes, isClientLink, fallBack } = this.props;
    const imageSrc = this.state.sourceError ? fallBack : image;

    return Object.entries(imageSizes)
      .reverse()
      .map(([breakpoint, breakpointConfig]) => {
        const urlConfig = parseUrlConfigParams(breakpointConfig);
        const { hidden } = breakpointConfig;

        return (
          <SourceTag
            key={breakpoint}
            imageSrc={get(imageSrc, 'value.src', '')}
            breakpoint={breakpoint}
            config={urlConfig.config}
            retinaConfig={urlConfig.retinaConfig}
            isClientLink={isClientLink}
            useRelativePath={this.state.sourceError}
            useBlank={hidden}
          />
        );
      });
  }

  getImageSource() {
    const { image, isClientLink, imageSizes } = this.props;
    const imageSource = get(image, 'value.src');
    let smallerImageConfig = null;

    if (imageSizes[375]) {
      smallerImageConfig = imageSizes[375];
    } else {
      smallerImageConfig = imageSizes[768] ? imageSizes[768] : imageSizes[1024];
    }

    const srcUrlConfig = smallerImageConfig
      ? parseUrlConfigParams(smallerImageConfig).config
      : '';

    return buildImagePath(srcUrlConfig, imageSource, isClientLink);
  }

  observerHandler = event => {
    if (event.isIntersecting) {
      this.setState({ visibility: true });
    }
  };

  handleImageError = () => {
    const fallBack = get(this.props, 'fallBack.value.src');

    if (!this.state.sourceError && Boolean(fallBack)) {
      this.setState({
        sourceError: true,
        imageSource: fallBack,
      });
    }
  };

  renderImage() {
    const {
      image,
      sitecoreContext,
      pictureCss,
      imageCss,
      editable,
      imageClassName,
      pictureClassName,
      isClientLink,
      fallbackAlt,
    } = this.props;

    if (!get(image, 'value.src')) {
      return null;
    }

    const isEditing = get(sitecoreContext, 'pageEditing');

    const shouldUseSitecoreSrc =
      isEditing || (!isEditing && isCloudinaryEnabled === 'false');

    if (shouldUseSitecoreSrc && !isClientLink) {
      return (
        <picture css={pictureCss} className={pictureClassName}>
          <Image
            className={imageClassName}
            css={imageCss}
            field={image}
            editable={editable}
          />
        </picture>
      );
    }

    const getAltValue = !isEmpty(get(image, 'value.alt'))
      ? image.value.alt
      : fallbackAlt;

    return (
      <picture css={pictureCss} className={pictureClassName}>
        {this.createSourceTags()}
        <img
          data-testid="rendered-image"
          className={imageClassName}
          css={imageCss}
          src={this.state.imageSource}
          alt={getAltValue}
          onError={this.handleImageError}
        />
      </picture>
    );
  }

  safeRender() {
    const { instantLoad } = this.props;
    const visible = this.state.visibility;
    const isEditing = get(this.props, 'sitecoreContext.pageEditing', false);

    // Check editing mode to avoid erros by content added dynamically in the
    // experience editor. (error: Cannot read property 'parent' of null).
    if (instantLoad || isEditing) {
      return this.renderImage();
    }
    return (
      <Observer onChange={e => this.observerHandler(e)}>
        {visible ? this.renderImage() : <span />}
      </Observer>
    );
  }
}

Picture.displayName = 'Picture';

Picture.defaultProps = {
  image: {},
  pictureCss: {},
  imageCss: {},
  imageSizes: {},
  isClientLink: false,
  children: {},
  editable: true,
  imageClassName: '',
  pictureClassName: '',
  fallbackAlt: '',
  fallBack: {},
};

Picture.propTypes = {
  image: PropTypes.any,
  sitecoreContext: PropTypes.any,
  pictureCss: PropTypes.any,
  imageCss: PropTypes.any,
  imageSizes: PropTypes.shape({
    width: PropTypes.number,
    height: PropTypes.number,
    crop: PropTypes.oneOf(['fill_pad', 'fill']),
    fetchFormat: PropTypes.string,
    gravity: PropTypes.oneOf(['auto:subject', 'north']),
    aspectRatio: PropTypes.string,
    quality: PropTypes.string,
  }),
  children: PropTypes.object,
  isClientLink: PropTypes.bool,
  editable: PropTypes.bool,
  imageClassName: PropTypes.string,
  pictureClassName: PropTypes.string,
  fallbackAlt: PropTypes.string,
  fallBack: PropTypes.object,
};

export default withSitecoreContext()(Picture);
